import { defineStore } from 'pinia';
import RequestsUtils from '@/helpers/RequestsUtils';
import { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';
import { ref } from 'vue';
import { useBranchesStore } from '@/store/BranchesStore';

type TagsChangeListener = (newTags: string[]) => void;
export type ListType = 'all' | 'content-filter-rules';
export const useTagsStore = defineStore('tags', () => {
  const branches = useBranchesStore();

  // Data
  const _tags = ref(null);
  const _list = ref([] as string[]);
  const _loading = ref(false);
  const _listPromiseResolve = ref(null);
  const _listPromiseReject = ref(null);
  const _listPromise = ref(null);
  const _listeners = ref([] as TagsChangeListener[]);
  const _listContentFilterRules = ref([] as string[]);
  const _loadingContentFilterRules = ref(false);
  const _listContentFilterRulesPromiseResolve = ref(null);
  const _listContentFilterRulesPromiseReject = ref(null);
  const _listContentFilterRulesPromise = ref(null);
  const _minTagLength = ref(3);
  const selectedTag = ref(null);
  const editorDocId = ref(null);
  const editorDocType = ref(null);

  // Methods
  function getTags(listType: ListType = 'all'): Promise<string[]> {
    if (listType === 'all') {
      if (_loading.value) {
        return _listPromise.value;
      } else if (_listPromise.value) {
        return new Promise((resolve) => {
          resolve(_list.value);
        });
      } else {
        return loadTags();
      }
    }
    if (listType === 'content-filter-rules') {
      return loadContentFilterRulesTags();
    }
  }

  async function loadTags() {
    if (!branches.selectedBranchId) {
      await branches.loadBranches();
    }
    if (_loading.value) {
      return _listPromise.value;
    }
    _loading.value = true;
    _listPromise.value = new Promise((resolve, reject) => {
      _listPromiseResolve.value = resolve;
      _listPromiseReject.value = reject;
    }) as Promise<string[]>;
    RequestsUtils.sendRequest({
      methodName: 'GET',
      url: 'db/system/tags',
    })
      .then((response: AxiosResponse) => {
        if (response?.data) {
          _tags.value = response.data;
          if (!_tags.value) {
            _tags.value = {};
          }
          if (!_tags.value?.neutral) {
            _tags.value.neutral = [];
          }
          if (!_tags.value?.malicious) {
            _tags.value.malicious = [];
          }
          if (!_tags.value?.legitimate) {
            _tags.value.legitimate = [];
          }
          _list.value = _.union(_tags.value.legitimate, _tags.value.neutral, _tags.value.malicious);
        }
        _loading.value = false;
        _listPromiseResolve.value(_list.value);
      })
      .catch((err: AxiosError) => {
        console.log('Error while attempting to load tags');
        console.log(err);
        _loading.value = false;
        _listPromiseReject.value(err);
      });
    return _listPromise.value;
  }

  async function loadContentFilterRulesTags() {
    if (!branches.selectedBranchId) {
      await branches.loadBranches();
    }
    if (_loadingContentFilterRules.value) {
      return _listContentFilterRulesPromise.value;
    }
    _loadingContentFilterRules.value = true;
    _listContentFilterRulesPromise.value = new Promise((resolve, reject) => {
      _listContentFilterRulesPromiseResolve.value = resolve;
      _listContentFilterRulesPromiseReject.value = reject;
    }) as Promise<string[]>;
    RequestsUtils.sendRequest({
      methodName: 'GET',
      url: `conf/${branches.selectedBranchId}/tags/doc/content-filter-rules`,
    })
      .then((response: AxiosResponse) => {
        if (response?.data) {
          _listContentFilterRules.value = response.data.items;
        }
        _loadingContentFilterRules.value = false;
        _listContentFilterRulesPromiseResolve.value(_listContentFilterRules.value);
      })
      .catch((err: AxiosError) => {
        console.log('Error while attempting to load Content Filter Rules tags');
        console.log(err);
        _loadingContentFilterRules.value = false;
        _listContentFilterRulesPromiseReject.value(err);
      });
    return _listContentFilterRulesPromise.value;
  }

  function addTag(newTag: string, listType: ListType = 'all') {
    newTag = normalizeTag(newTag);
    let listRef;
    if (listType === 'all') {
      listRef = _list;
    }
    if (listType === 'content-filter-rules') {
      listRef = _listContentFilterRules;
    }
    const tagExists = _.some(listRef.value, (tag: string) => {
      return tag.toLowerCase() === newTag;
    });
    if (tagExists || newTag.length < _minTagLength.value) {
      return;
    }
    listRef.value.push(newTag);
    if (listType === 'all' && _tags.value?.neutral) {
      _tags.value.neutral.push(newTag);
      // save to DB
      RequestsUtils.sendRequest({
        methodName: 'PUT',
        url: 'db/system/tags',
        data: _tags.value,
      });
      updateListeners(listType);
    }
    return newTag;
  }

  function textToTag(text: string): string {
    return (
      text
        ?.trim()
        ?.replace(/[^a-zA-Z0-9]/g, '-')
        ?.toLowerCase() || ''
    );
  }

  function normalizeTag(tag: string): string {
    const colonIndex = tag.indexOf(':');
    if (colonIndex === -1) {
      return textToTag(tag);
    }
    return `${textToTag(tag.slice(0, colonIndex))}:${textToTag(tag.slice(colonIndex + 1))}`;
  }

  function updateListeners(listType: ListType = 'all') {
    if (listType === 'all') {
      _.forEach(_listeners.value, (listener) => {
        listener(_list.value);
      });
    }
  }

  function addTagsChangeListener(listenerToAdd: TagsChangeListener, listType: ListType = 'all') {
    if (listType === 'all') {
      _listeners.value.push(listenerToAdd);
    }
  }

  function removeTagsChangeListener(
    listenerToRemove: TagsChangeListener,
    listType: ListType = 'all'
  ) {
    if (listType === 'all') {
      _listeners.value = _listeners.value.filter((listener) => {
        return !_.isEqual(listener, listenerToRemove);
      });
    }
  }

  function setEditorDocId(docId: string) {
    editorDocId.value = docId;
  }

  function setEditorDocType(docType: string) {
    editorDocType.value = docType;
  }

  return {
    getTags,
    loadTags,
    loadContentFilterRulesTags,
    addTag,
    textToTag,
    normalizeTag,
    addTagsChangeListener,
    removeTagsChangeListener,
    selectedTag,
    editorDocId,
    editorDocType,
    setEditorDocId,
    setEditorDocType,
  };
});
